import { useContext, useEffect, useState, MouseEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FirebaseError } from 'firebase/app';
import Dialog from '@mui/material/Dialog';
import { Box } from '@mui/material';
import { MetadataError } from '@ep/error-handling';
import {
  signInWithPopup,
  fetchSignInMethodsForEmail,
  OAuthCredential,
  OAuthProvider,
  User,
  UserCredential
} from 'firebase/auth';
import logo from '../../../assets/syntiro-logo-horizontal-blue-text.svg';
import VerifyEmail from './VerifyEmail';
import VerifySmsMfa from './VerifySmsMfa';
import LoginIdentifier from './LoginIdentifier';
import LoginPassword from './LoginPassword';
import EnrollSmsMfa from './EnrollSmsMfa';
import RecoverAccount from './RecoverAccount';
import { MfaMethodSelector } from './MfaMethodSelector';
import EnrollAuthenticatorMfa from './EnrollAuthenticatorMfa';
import VerifyAuthenticator from './VerifyAuthenticator';
import LoginError from './LoginError';
import LinkAccountsFederated from './LinkAccountsFederated';
import LinkAccountsPassword from './LinkAccountsPassword';
import {
  auth,
  clearRecaptchaData,
  createMicrosoftProvider,
  setSsoExpectedEmail
} from '../../../lib/google/firebase';
import type { MicrosoftProviderOptions } from '../../../lib/google/firebase';
import { log } from '../../../util/log';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import { generateUuidV4 } from '../../../util/uuid';
import { getTenantDomainAuthMethodFromEmail } from '../../../lib/auth/auth.util';
import { TenantAuthMethod } from '@esg/esg-global-types';

interface PropTypes {
  setShowLoginModal(state: boolean): void;
  setAuthenticated(authenticated: boolean): void;
  showLoginModal: boolean;
}

export type LoginState =
  | 'login_identifier'
  | 'login_password'
  | 'verify_email'
  | 'verify_mfa'
  | 'verify_authenticator'
  | 'enroll_authenticator_mfa'
  | 'enroll_sms_mfa'
  | 'recovery'
  | 'mfa_selection'
  | 'login_link_federated'
  | 'login_link_password'
  | 'login_error';

/**
 * Handles the state switching of the sign in processes modals.
 * @param {(authenticated: boolean): void} setAuthenticated Sets the global authenticated state of the application.
 * @param {(state: boolean): void} setShowLoginModal Sets the state of if the login modal is shown or not.
 * @param {boolean} showLoginModal The current state of if the login modal is shown or not.
 * @returns {JSX.Element}
 */
const LoginModal = (props: PropTypes): JSX.Element => {
  const { setFeedbackData } = useContext(FeedbackSnackbarContext);
  const { setShowLoginModal } = props;
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState<string>('');
  const [signInUser, setSignInUser] = useState<User | null>(null);
  const [loginState, setLoginState] = useState<LoginState>('login_identifier');
  const [loginErrorMessage, setLoginErrorMessage] = useState<string>('');
  const [verifyTimeout, setVerifyTimeout] = useState<number>(0);
  const [showRecaptchaContainer, setShowRecaptchaContainer] = useState<boolean>(false);
  const [pendingMicrosoftCredential, setPendingMicrosoftCredential] =
    useState<OAuthCredential | null>(null);
  const [linkAccountEmail, setLinkAccountEmail] = useState<string>('');

  // Display error modal with given error message
  const displayErrorModal = (message: string): void => {
    setLoginErrorMessage(message);
    setLoginState('login_error');
    props.setShowLoginModal(true);
  };

  // Handles debounce time of user entering the code into the modal.
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (verifyTimeout > 0) {
      timer = setTimeout(() => {
        setVerifyTimeout(verifyTimeout - 1);
      }, 1000);
    }
    return () => clearTimeout(timer);
  }, [verifyTimeout]);

  // Handles closing of login modal and resetting login flow to initial sign in state.
  const handleCloseLoginModal = (): void => {
    setLoginErrorMessage('');
    setEmail('');
    setLoginState('login_identifier');
    clearRecaptchaData();
    props.setShowLoginModal(false);
    if (location.pathname === '/recover-password') {
      navigate('/');
    }
  };

  const sso_error_message_map: Record<string, string> = {
    'invalid-tenant-domain':
      'This domain is not configured to use Microsoft Single Sign-On. Please sign in with your Syntiro password or contact us for more information.',
    'non-provisioned-user':
      'It appears you do not have a Syntiro account yet. Please contact us to request an account.',
    'deactivated-user': 'Your account has been deactivated.'
  };

  const handleMicrosoftSignIn = async (
    event: MouseEvent,
    options: MicrosoftProviderOptions = {}
  ): Promise<void> => {
    event?.preventDefault();
    // When the account picker is forced the user is deliberately choosing a different
    // account, so no email is expected and the mismatch guard must not run.
    const expected_email = options.force_account_picker ? '' : email.toLowerCase();
    setSsoExpectedEmail(expected_email || null);
    handleCloseLoginModal();
    try {
      const result: UserCredential = await signInWithPopup(
        auth,
        createMicrosoftProvider({
          ...options,
          login_hint: options.force_account_picker ? undefined : expected_email || undefined
        })
      );
      if (expected_email && result.user.email?.toLowerCase() !== expected_email) {
        setFeedbackData({
          message: `The Microsoft account signed in does not match ${expected_email}. Use "Sign in with a different Microsoft account" to choose another account.`,
          state: true,
          type: 'error'
        });
      }
    } catch (err: unknown) {
      setSsoExpectedEmail(null);
      // Handle gracefully when popup is manually closed by the user.
      if (
        (err as FirebaseError).code === 'auth/popup-closed-by-user' ||
        (err as FirebaseError).code === 'auth/cancelled-popup-request'
      ) {
        return;
      }

      // Handle migrating password users to federated provider for configured tenant domain.
      if ((err as FirebaseError).code === 'auth/account-exists-with-different-credential') {
        const pending_credential: OAuthCredential | null = OAuthProvider.credentialFromError(
          err as FirebaseError
        );
        const collision_email: string = ((err as FirebaseError).customData?.email as string) ?? '';
        const auth_method: TenantAuthMethod | null =
          await getTenantDomainAuthMethodFromEmail(collision_email);
        if (auth_method === 'microsoft') {
          setPendingMicrosoftCredential(pending_credential);
          setLinkAccountEmail(collision_email);
          setLoginState('login_link_federated');
          props.setShowLoginModal(true);
          return;
        }
        displayErrorModal(sso_error_message_map['invalid-tenant-domain']);
        return;
      }

      const firebase_message: string = (err as FirebaseError).message ?? '';

      // Handle migrating federated provider users to password for unconfigured tenant domain.
      if (firebase_message.includes('invalid-tenant-domain')) {
        const email_match: RegExpMatchArray | null = firebase_message.match(
          /invalid-tenant-domain:([^\s"\\}]+)/
        );
        // The picked account is unknown to us when the account picker was forced, so the
        // email carried on the blocking function error is the authoritative one.
        const error_email: string = options.force_account_picker
          ? email_match?.[1] || ''
          : email || email_match?.[1] || '';
        if (!error_email) return;
        const auth_method: TenantAuthMethod | null =
          await getTenantDomainAuthMethodFromEmail(error_email);
        if (auth_method === 'email_password') {
          const sign_in_methods: Array<string> = await fetchSignInMethodsForEmail(
            auth,
            error_email
          );
          if (sign_in_methods.includes('microsoft.com') && !sign_in_methods.includes('password')) {
            setLinkAccountEmail(error_email);
            setLoginState('login_link_password');
            props.setShowLoginModal(true);
            return;
          }
        }
      }

      // Display alternative error flow state received from blocking function.
      const matched_error_code: string | undefined = Object.keys(sso_error_message_map).find(
        (code: string) => firebase_message.includes(code)
      );
      if (matched_error_code) {
        displayErrorModal(sso_error_message_map[matched_error_code]);
        return;
      }

      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : 'Microsoft SSO sign-in failed.',
          {},
          tracking_id
        )
      );
      setFeedbackData({
        message: `Failed to sign in with Microsoft. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
  };

  useEffect(() => {
    if (!showRecaptchaContainer) {
      clearRecaptchaData();
    }
  }, [showRecaptchaContainer]);

  // Auto-open recovery panel when entering via /recover-password route.
  useEffect(() => {
    if (location.pathname === '/recover-password' && !props.showLoginModal) {
      setLoginState('recovery');
      setShowLoginModal(true);
    }
  }, [location.pathname, setShowLoginModal]);

  return (
    <div>
      <Dialog
        open={props.showLoginModal}
        onClose={handleCloseLoginModal}
        sx={{ maxWidth: 500, m: 'auto' }}
      >
        <Box sx={{ p: 5 }}>
          {showRecaptchaContainer && <Box id="recaptcha-container"></Box>}
          <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
            <img src={logo} alt="Syntiro" width={275} />
          </Box>
          {loginState === 'login_identifier' && (
            <LoginIdentifier
              data-testid="sign-in-button"
              email={email}
              setSignInUser={(user: User | null) => setSignInUser(user)}
              handleLoginState={(login_state: LoginState) => setLoginState(login_state)}
              resetVerifyTimeout={(seconds) => setVerifyTimeout(seconds)}
              setShowRecaptchaContainer={(state: boolean) => setShowRecaptchaContainer(state)}
              handleMicrosoftSignIn={handleMicrosoftSignIn}
              setEmail={setEmail}
            />
          )}
          {loginState === 'login_password' && (
            <LoginPassword
              email={email}
              setSignInUser={(user: User | null) => setSignInUser(user)}
              handleLoginState={(login_state: LoginState) => setLoginState(login_state)}
              resetVerifyTimeout={(seconds) => setVerifyTimeout(seconds)}
              setShowRecaptchaContainer={(state: boolean) => setShowRecaptchaContainer(state)}
            />
          )}
          {loginState === 'verify_email' && (
            <VerifyEmail
              resetVerifyTimeout={() => setVerifyTimeout(60)}
              sign_in_user={signInUser}
              verify_timeout={verifyTimeout}
              returnToSignIn={() => setLoginState('login_identifier')}
            />
          )}
          {loginState === 'mfa_selection' && (
            <MfaMethodSelector
              handleLoginState={(login_state: LoginState) => setLoginState(login_state)}
              closeLogin={() => {
                setEmail('');
                props.setShowLoginModal(false);
                setLoginState('login_identifier');
              }}
            />
          )}
          {loginState === 'verify_mfa' && (
            <VerifySmsMfa
              resetVerifyTimeout={() => setVerifyTimeout(30)}
              verify_timeout={verifyTimeout}
              setAuthenticated={(authenticated: boolean) => props.setAuthenticated(authenticated)}
              closeLogin={() => {
                setEmail('');
                props.setShowLoginModal(false);
                setLoginState('login_identifier');
              }}
            />
          )}
          {loginState === 'enroll_sms_mfa' && (
            <EnrollSmsMfa
              handleLoginState={(login_state: LoginState) => setLoginState(login_state)}
              setAuthenticated={(authenticated: boolean) => props.setAuthenticated(authenticated)}
              closeLogin={() => {
                setEmail('');
                props.setShowLoginModal(false);
                setLoginState('login_identifier');
              }}
            />
          )}

          {loginState === 'enroll_authenticator_mfa' && (
            <EnrollAuthenticatorMfa
              handleLoginState={(login_state: LoginState) => setLoginState(login_state)}
              setAuthenticated={(authenticated: boolean) => props.setAuthenticated(authenticated)}
              closeLogin={() => {
                setEmail('');
                props.setShowLoginModal(false);
                setLoginState('login_identifier');
              }}
            />
          )}

          {loginState === 'verify_authenticator' && (
            <VerifyAuthenticator
              handleLoginState={(login_state: LoginState) => setLoginState(login_state)}
              setAuthenticated={(authenticated: boolean) => props.setAuthenticated(authenticated)}
              closeLogin={() => {
                setEmail('');
                props.setShowLoginModal(false);
                setLoginState('login_identifier');
              }}
            />
          )}
          {loginState === 'recovery' && (
            <RecoverAccount returnToSignIn={() => setLoginState('login_identifier')} />
          )}
          {loginState === 'login_link_password' && (
            <LinkAccountsPassword
              email={linkAccountEmail || email}
              returnToPassword={() => setLoginState('login_identifier')}
            />
          )}
          {loginState === 'login_link_federated' && (
            <LinkAccountsFederated
              email={linkAccountEmail}
              pending_credential={pendingMicrosoftCredential}
              returnToIdentifier={() => setLoginState('login_identifier')}
            />
          )}
          {loginState === 'login_error' && (
            <LoginError
              message={loginErrorMessage}
              handleBackClick={() => {
                setLoginErrorMessage('');
                setLoginState('login_identifier');
              }}
            />
          )}
        </Box>
      </Dialog>
    </div>
  );
};

export default LoginModal;
